import { Outlet, useLocation, useNavigate } from "react-router";
import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from "react";
import { flushSync } from "react-dom";
import {
  ASSISTANT_MAX_WIDTH,
  AssistantPanel,
  PANEL_COVERS_PAGE_QUERY,
  buildAssistantAppState,
} from "@bastion/assistant";
import { SidebarProvider } from "@bastion/sidebar";
import { useDashboardAssistantContextStore, useEpisodeDisplayStore, useEpisodeFiltersStore } from "@bastion/stores";
import { PHONE_QUERY, TOUCH_QUERY, useMediaQuery } from "@bastion/ui";
import { readWorkflowFiltersFromLocation } from "./readWorkflowFilters";
import { flattenEpisodeFiltersForAssistant } from "@bastion/episodes";
import { readEpisodeFiltersFromSearch } from "./readEpisodeFilters";
import { registerWebToolRenderers } from "@/shared/assistant/registerRenderers";
import { AppHeader } from "./AppHeader";
import { AppRail } from "./AppRail";
import { AppSecondarySidebar } from "./AppSecondarySidebar";
import { CommandPalette } from "./CommandPalette";
import { phoneViewOf, withPhoneView, withoutPhoneView, type PhoneView } from "./phoneViews";
import { useSecondarySidebar } from "./useSecondarySidebar";
import { useSecondarySidebarWidth } from "./useSecondarySidebarWidth";
import { AppHistoryProvider } from "./useAppHistory";
import type { RightPanelTab, RightPanelWorkspaceContext } from "./rightPanelWorkspace";
import {
  assistantNavigationRef,
  navigateRef,
  registerWebToolExecutors,
  routeToTabId,
  type AssistantNavigationRequest,
} from "../assistantTools";
import styles from "./AppLayout.module.css";

/** The icon rail is the collapsed sidebar of the design system (64px). */
const APP_RAIL_WIDTH = "var(--app-side-collapsed)";

/** The wide right panel takes this part of the window (`toggleWide`). */
const WIDE_PANEL_SHARE = 0.6;

export function AppLayout() {
  return (
    <SidebarProvider>
      <AppHistoryProvider>
        <AppLayoutInner />
      </AppHistoryProvider>
    </SidebarProvider>
  );
}

function AppLayoutInner() {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const dashboardAssistantContext = useDashboardAssistantContextStore((s) => s.context);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const isTouch = useMediaQuery(TOUCH_QUERY);
  const panelCoversPage = useMediaQuery(PANEL_COVERS_PAGE_QUERY);
  // On a phone the navigation drawer and the right panel open as history
  // entries (phoneViews.ts), so Back and any navigation close them.
  const phoneView = isPhone ? phoneViewOf(location.state) : null;

  const navigate = useCallback((path: string) => {
    routerNavigate(path);
  }, [routerNavigate]);

  const getAppState = useCallback(
    (): Record<string, unknown> => ({
      ...buildAssistantAppState({
        activeTab: routeToTabId(location.pathname),
        workspacePath: "",
        platform: "web",
        episodeFilters: location.pathname.startsWith("/episodes")
          ? {
              ...readEpisodeFiltersFromSearch(location.search),
              currentPage: useEpisodeFiltersStore.getState().currentPage,
            }
          : flattenEpisodeFiltersForAssistant(
              useEpisodeFiltersStore.getState().filters,
              {
                viewMode: useEpisodeDisplayStore.getState().viewMode,
                sort: useEpisodeDisplayStore.getState().sort,
                pageSize: useEpisodeDisplayStore.getState().pageSize,
              },
              useEpisodeFiltersStore.getState().currentPage,
            ),
        workflowFilters: readWorkflowFiltersFromLocation(
          location.pathname,
          location.search,
        ),
        chatSurface: "sidebar",
        dashboardContext: dashboardAssistantContext ?? undefined,
      }),
    }),
    [location.pathname, location.search, dashboardAssistantContext],
  );

  const [assistantOpen, setAssistantOpen] = useState(false);
  const panelOpen = isPhone ? phoneView === "panel" : assistantOpen;
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // The values that the callbacks below read. The callbacks keep one
  // identity, so the effects of the pages that use them do not run again.
  // A layout effect updates the values before the effects of the pages run.
  const latestRef = useRef({
    isPhone,
    isTouch,
    location,
    navigate: routerNavigate,
    commandPaletteOpen,
  });
  useLayoutEffect(() => {
    latestRef.current = {
      isPhone,
      isTouch,
      location,
      navigate: routerNavigate,
      commandPaletteOpen,
    };
  });

  /**
   * Opens a phone view as a new history entry with the same URL. A view that
   * is open gives its entry to the new view.
   */
  const openPhoneView = useCallback((view: PhoneView) => {
    const latest = latestRef.current;
    const current = phoneViewOf(latest.location.state);
    if (current === view) return;
    const state = withPhoneView(latest.location.state, view);
    latest.navigate(
      {
        pathname: latest.location.pathname,
        search: latest.location.search,
        hash: latest.location.hash,
      },
      { replace: current !== null, state },
    );
    // The router renders the new entry in a transition. Until then a second
    // tap reads the view as open.
    latest.location = { ...latest.location, state };
  }, []);

  /** Closes a phone view: Back to the entry under it. */
  const closePhoneView = useCallback((view: PhoneView) => {
    const latest = latestRef.current;
    if (phoneViewOf(latest.location.state) !== view) return;
    latest.navigate(-1);
    latest.location = { ...latest.location, state: withoutPhoneView(latest.location.state) };
  }, []);

  /** Opens or closes the right panel on a phone. Wider, it is React state. */
  const setPhonePanelOpen = useCallback(
    (open: boolean) => {
      if (open) openPhoneView("panel");
      else closePhoneView("panel");
    },
    [closePhoneView, openPhoneView],
  );

  const [assistantWidth, setAssistantWidth] = useState<number | null>(null);
  // The width before the panel became wide, or null while it is not wide.
  const [widthBeforeWide, setWidthBeforeWide] = useState<{ width: number | null } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [rightPanelTabs, setRightPanelTabs] = useState<RightPanelTab[]>([]);
  const [activeRightPanelTabId, setActiveRightPanelTabId] = useState("chat");
  const [panelHandoffSessionId, setPanelHandoffSessionId] = useState<string | null>(null);
  const toggleAssistant = useCallback(() => {
    if (latestRef.current.isPhone) {
      setPhonePanelOpen(phoneViewOf(latestRef.current.location.state) !== "panel");
      return;
    }
    setAssistantOpen((isOpen) => !isOpen);
  }, [setPhonePanelOpen]);

  useEffect(() => {
    if (
      activeRightPanelTabId !== "chat" &&
      !rightPanelTabs.some((tab) => tab.id === activeRightPanelTabId)
    ) {
      setActiveRightPanelTabId("chat");
    }
  }, [activeRightPanelTabId, rightPanelTabs]);

  const openRightPanelTab = useCallback((tabId: string) => {
    setActiveRightPanelTabId(tabId);
    if (latestRef.current.isPhone) {
      setPhonePanelOpen(true);
      return;
    }
    setAssistantOpen(true);
  }, [setPhonePanelOpen]);

  // A page that unmounts can call this after a navigation. On a phone the
  // new entry has no panel, so the call then does nothing.
  const closeRightPanel = useCallback(() => {
    if (latestRef.current.isPhone) {
      setPhonePanelOpen(false);
      return;
    }
    setAssistantOpen(false);
  }, [setPhonePanelOpen]);

  const toggleRightPanelTab = useCallback(
    (tabId: string) => {
      if (latestRef.current.isPhone) {
        const isOpen = phoneViewOf(latestRef.current.location.state) === "panel";
        if (isOpen && activeRightPanelTabId === tabId) {
          setPhonePanelOpen(false);
          return;
        }
        setActiveRightPanelTabId(tabId);
        setPhonePanelOpen(true);
        return;
      }
      // If the requested tab is already the active one AND the panel is open,
      // treat the request as "dismiss" so a hotkey like R can both summon and
      // dismiss the Review tab. Otherwise, surface the requested tab.
      setAssistantOpen((wasOpen) => {
        if (wasOpen && activeRightPanelTabId === tabId) {
          return false;
        }
        setActiveRightPanelTabId(tabId);
        return true;
      });
    },
    [activeRightPanelTabId, setPhonePanelOpen],
  );

  // toggleWide reads the width from a ref. Thus a drag of the panel edge
  // does not give the right panel context a new value on each frame.
  const assistantWidthRef = useRef(assistantWidth);
  useEffect(() => {
    assistantWidthRef.current = assistantWidth;
  }, [assistantWidth]);

  const toggleWide = useCallback(() => {
    if (widthBeforeWide) {
      setAssistantWidth(widthBeforeWide.width);
      setWidthBeforeWide(null);
    } else {
      setWidthBeforeWide({ width: assistantWidthRef.current });
      setAssistantWidth(
        Math.min(ASSISTANT_MAX_WIDTH, Math.round(window.innerWidth * WIDE_PANEL_SHARE)),
      );
    }
  }, [widthBeforeWide]);

  const rightPanelContext = useMemo<RightPanelWorkspaceContext>(
    () => ({
      setTabs: setRightPanelTabs,
      openTab: openRightPanelTab,
      closePanel: closeRightPanel,
      toggleTab: toggleRightPanelTab,
      activeTabId: activeRightPanelTabId,
      isOpen: panelOpen,
      isWide: widthBeforeWide !== null,
      toggleWide,
    }),
    [
      activeRightPanelTabId,
      panelOpen,
      closeRightPanel,
      openRightPanelTab,
      toggleRightPanelTab,
      toggleWide,
      widthBeforeWide,
    ],
  );

  const handleAssistantNavigation = useCallback(
    (request: AssistantNavigationRequest) => {
      routerNavigate(request.targetPath);
      request.afterNavigate?.();
      setPanelHandoffSessionId(request.sessionIdToHandoff);
      setActiveRightPanelTabId("chat");
      // On a phone the panel would cover the page that Gauss opened. The
      // handoff waits until the reader opens the panel again.
      if (latestRef.current.isPhone) return;
      setAssistantOpen(true);
    },
    [routerNavigate],
  );

  useEffect(() => {
    registerWebToolExecutors();
    registerWebToolRenderers();
    navigateRef.current = navigate;
    assistantNavigationRef.current = handleAssistantNavigation;
    return () => {
      if (navigateRef.current === navigate) {
        navigateRef.current = null;
      }
      if (assistantNavigationRef.current === handleAssistantNavigation) {
        assistantNavigationRef.current = null;
      }
    };
  }, [handleAssistantNavigation, navigate]);

  const handlePanelHandoffConsumed = useCallback(() => {
    setPanelHandoffSessionId(null);
  }, []);

  const handleViewAllChats = useCallback(() => {
    if (!latestRef.current.isPhone) {
      navigate("/");
      return;
    }
    // On a phone the chat list is in the navigation drawer of the home
    // page. Home replaces the entry of the panel, and the drawer opens over
    // it, so Back closes the drawer first.
    routerNavigate("/", { replace: true });
    routerNavigate("/", { state: withPhoneView(null, "sideNav") });
  }, [navigate, routerNavigate]);

  // On a phone the open palette also holds a history entry, so Back closes
  // it. Its open state stays React state: the palette renders in the same
  // task as the tap, so iOS raises the keyboard for its input.
  // "pushed": the entry is added. "shown": the location shows the entry.
  const paletteEntryRef = useRef<"none" | "pushed" | "shown">("none");

  const closeCommandPalette = useCallback(() => {
    setCommandPaletteOpen(false);
    if (paletteEntryRef.current !== "none") {
      paletteEntryRef.current = "none";
      closePhoneView("palette");
    }
  }, [closePhoneView]);

  const toggleCommandPalette = useCallback(() => {
    const { isPhone: phone, isTouch: touch, commandPaletteOpen: open } = latestRef.current;
    if (!phone && !touch) {
      setCommandPaletteOpen((isOpen) => !isOpen);
      return;
    }
    if (open) {
      closeCommandPalette();
      return;
    }
    // iOS raises the keyboard only for a focus in the task of the tap, so
    // the palette and its input render now.
    flushSync(() => setCommandPaletteOpen(true));
    if (phone) {
      paletteEntryRef.current = "pushed";
      openPhoneView("palette");
    }
  }, [closeCommandPalette, openPhoneView]);

  useEffect(() => {
    if (paletteEntryRef.current === "none") return;
    if (phoneViewOf(location.state) === "palette") {
      paletteEntryRef.current = "shown";
    } else if (paletteEntryRef.current === "shown") {
      // Back left the entry of the palette.
      paletteEntryRef.current = "none";
      setCommandPaletteOpen(false);
    }
  }, [location.state]);

  const navigateFromCommandPalette = useCallback(
    (path: string) => {
      if (paletteEntryRef.current === "none") {
        navigate(path);
        return;
      }
      // The page takes the entry of the palette, so Back skips the palette.
      paletteEntryRef.current = "none";
      routerNavigate(path, { replace: true });
    },
    [navigate, routerNavigate],
  );

  const { isOpen: savedSecondarySidebarOpen, toggle: toggleSecondarySidebar } =
    useSecondarySidebar();
  const { widthPx: secondarySidebarWidthPx, setWidthPx, resetWidthPx } =
    useSecondarySidebarWidth();
  // On a phone the section sidebar is in the navigation drawer. Wider, it
  // keeps the saved toggle.
  const navDrawerOpen = phoneView === "sideNav";
  const secondarySidebarOpen = isPhone ? navDrawerOpen : savedSecondarySidebarOpen;

  const toggleSideNav = useCallback(() => {
    if (!latestRef.current.isPhone) {
      toggleSecondarySidebar();
      return;
    }
    if (phoneViewOf(latestRef.current.location.state) === "sideNav") {
      closePhoneView("sideNav");
    } else {
      openPhoneView("sideNav");
    }
  }, [closePhoneView, openPhoneView, toggleSecondarySidebar]);

  const closeNavDrawer = useCallback(() => closePhoneView("sideNav"), [closePhoneView]);

  // A link in the open drawer takes the entry of the drawer, so Back from
  // the new page does not open the drawer again.
  const navigateFromSideNav = useCallback(
    (path: string) => {
      const { isPhone: phone, location: current } = latestRef.current;
      if (phone && phoneViewOf(current.state) === "sideNav") {
        routerNavigate(path, { replace: true });
        return;
      }
      navigate(path);
    },
    [navigate, routerNavigate],
  );

  // A reload keeps the state of the history entry. The app opens with no
  // phone view, so a drawer or a sheet does not cover the page at load.
  useEffect(() => {
    const { location: loaded, navigate: go } = latestRef.current;
    if (phoneViewOf(loaded.state) === null) return;
    go(
      { pathname: loaded.pathname, search: loaded.search, hash: loaded.hash },
      { replace: true, state: withoutPhoneView(loaded.state) },
    );
  }, []);

  // A layer over the page takes the focus from the page (inert). Where the
  // right panel is a drawer, the top bar stays live. The phone sheet and the
  // navigation drawer cover the top bar too.
  const panelIsLayer = panelOpen && panelCoversPage;
  const pageInert = navDrawerOpen || panelIsLayer;
  const headerInert = navDrawerOpen || (isPhone && panelOpen);

  // Esc closes the top layer: the navigation drawer, then the right panel
  // where it covers the page. A menu or a dialog that handles Esc marks the
  // event, and the event then closes only that menu or dialog.
  useEffect(() => {
    if (!navDrawerOpen && !panelIsLayer) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (navDrawerOpen) closeNavDrawer();
      else closeRightPanel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeNavDrawer, closeRightPanel, navDrawerOpen, panelIsLayer]);

  const shellStyle = useMemo(
    () =>
      ({
        "--app-rail-width": APP_RAIL_WIDTH,
        "--app-secondary-sidebar-inner-width": `${secondarySidebarWidthPx}px`,
        "--app-secondary-sidebar-width": secondarySidebarOpen
          ? `${secondarySidebarWidthPx}px`
          : "0px",
      }) as CSSProperties,
    [secondarySidebarOpen, secondarySidebarWidthPx],
  );

  return (
    <div
      className={styles.shell}
      data-testid="app-shell"
      style={shellStyle}
    >
      <AppHeader
        isSecondarySidebarOpen={secondarySidebarOpen}
        onToggleSecondarySidebar={toggleSideNav}
        isAssistantOpen={panelOpen}
        onAssistantToggle={toggleAssistant}
        hasAssistantHandoff={panelHandoffSessionId !== null && !panelOpen}
        onToggleCommandPalette={toggleCommandPalette}
        inert={headerInert}
      />
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={closeCommandPalette}
        onNavigate={navigateFromCommandPalette}
      />
      <div className={styles.body}>
        <div
          className={styles.sideNav}
          data-open={navDrawerOpen ? "true" : "false"}
          inert={panelIsLayer}
        >
          <AppRail pathname={location.pathname} inDrawer={isPhone} />
          <AppSecondarySidebar
            pathname={location.pathname}
            isOpen={secondarySidebarOpen}
            inDrawer={isPhone}
            onNavigate={navigateFromSideNav}
            onSidebarWidthChange={setWidthPx}
            onSidebarWidthReset={resetWidthPx}
          />
        </div>
        {navDrawerOpen ? (
          <button
            type="button"
            className={styles.navScrim}
            onClick={closeNavDrawer}
            aria-label="Close navigation"
          />
        ) : null}
        <div className={styles.main} inert={pageInert}>
          <main className={styles.content}>
            <Outlet context={rightPanelContext} />
          </main>
        </div>
        <AssistantPanel
          isOpen={panelOpen}
          onToggle={toggleAssistant}
          width={assistantWidth}
          onWidthChange={setAssistantWidth}
          isResizing={isResizing}
          onResizingChange={setIsResizing}
          getAppState={getAppState}
          dashboardAssistantActive={Boolean(dashboardAssistantContext)}
          dashboardAssistantLabel={dashboardAssistantContext?.title ?? null}
          handoffSessionId={panelHandoffSessionId}
          onHandoffConsumed={handlePanelHandoffConsumed}
          onViewAllChats={handleViewAllChats}
          extraTabs={rightPanelTabs}
          activeTabId={activeRightPanelTabId}
          onActiveTabChange={setActiveRightPanelTabId}
          overlayBelowWide
        />
      </div>
    </div>
  );
}
